import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import {
  Alert,
  Button,
  Row,
  Typography,
  Space,
  Badge,
  Flex,
  Spin,
  Popconfirm,
} from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';

import Layout from '../../../../components/layout';
import Breadcrumb from '../../../../components/common/breadcrumb';
import JobDetailsEdit from '../../components/job-details-edit';
import SkillsExperience from '../../components/skills-experience';
import TrainingWheelsHint from '../../../../components/common/training-wheels-hint';
import {
  isMatchingPaused,
  matchingNotice,
} from '../../utils/matchingNotice';
import HiringManagerModal from '../../components/hiring-manager-modal';

import styles from './index.module.css';
import { useJobCandidates } from './useJobCandidates';
import {
  summarisePipeline,
  distributeScores,
} from '../../../dashboard/utils/aggregate';
import { Job, JobPayload, HiringManager } from '../../../../fsm/jobs';
import { usePermissions } from '../../../../context/permission.context';
import { User } from '../../../../fsm/users';

interface Props {
  user?: User;
  data: Job | null;
  isLoading: boolean;
  hiringManagers: HiringManager[];
  onFetchDetails: (id: number) => void;
  onFetchHiringManagers: () => void;
  onFetchRecruiters: () => void;
  onAssignHiringManagers: (id: number, hiringManagerId: number) => void;
  onAssignRecruiter: (id: number, recruiterId: number) => void;
  onUpdateDetails: (id: number, job: JobPayload) => void;
}

const { Title, Text } = Typography;

const JobDetails = ({
  user,
  data,
  isLoading,
  hiringManagers,
  onFetchDetails,
  onUpdateDetails,
  onFetchHiringManagers,
  onFetchRecruiters,
  onAssignHiringManagers,
  onAssignRecruiter,
}: Props) => {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isOpenStatusConfirmOpen, setIsOpenStatusConfirmOpen] = useState(false);
  const [isCloseStatusConfirmOpen, setIsCloseStatusConfirmOpen] =
    useState(false);
  const { jobId } = useParams();
  const candidates = useJobCandidates(jobId);
  const { canSeeJobAssignHMCard } = usePermissions();

  useEffect(() => {
    if (jobId) {
      onFetchDetails(parseInt(jobId));
    }
  }, [jobId]);

  const handleModalOpen = useCallback(() => {
    setIsModalOpen(true);
  }, [setIsModalOpen]);

  const handleModalClose = useCallback(() => {
    setIsModalOpen(false);
  }, [setIsModalOpen]);

  const handleJobUpdate = useCallback(
    (job: JobPayload) => {
      if (jobId) {
        const payload: JobPayload = { ...job };
        delete payload.id;
        delete payload.externalId;
        delete payload.customer;
        delete payload.createdBy;
        delete payload.salaryCurrency;
        delete payload.visaRequirements;
        delete payload.statusChangedAt;
        delete payload.createdAt;
        delete payload.updatedAt;

        onUpdateDetails(parseInt(jobId), payload);
      }
    },
    [jobId, onUpdateDetails],
  );

  const showOpenStatusPopconfirm = useCallback(() => {
    setIsOpenStatusConfirmOpen(true);
  }, []);

  const handleOpenStatusCancel = useCallback(() => {
    setIsOpenStatusConfirmOpen(false);
  }, []);

  const showCloseStatusPopconfirm = useCallback(() => {
    setIsCloseStatusConfirmOpen(true);
  }, []);

  const handleCloseStatusCancel = useCallback(() => {
    setIsCloseStatusConfirmOpen(false);
  }, []);

  const handleUpdate = useCallback(
    (props: Partial<Job>) => () => {
      handleJobUpdate({
        ...data,
        ...props,
      });
      setIsCloseStatusConfirmOpen(false);
      setIsOpenStatusConfirmOpen(false);
    },
    [data, handleJobUpdate],
  );

  const ProductManagerCard = useMemo(() => {
    return (
      <div className={styles.productManagerCard}>
        <Space direction="vertical" size={0}>
          <Title level={2} className={styles.sectionTitle}>
            {data?.title}
          </Title>
          <Space size={20}>
            <Text type="secondary" className={styles.sectionHint}>
              Posted day:{' '}
              {data?.postedAt
                ? new Intl.DateTimeFormat('en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                    hour: 'numeric',
                    minute: 'numeric',
                  }).format(new Date((data?.postedAt as number) * 1000))
                : 'No date'}
            </Text>
            <Space>
              {data?.status === 'OPEN' && (
                <>
                  <Badge status="success" /> {'Open'}
                </>
              )}
              {data?.status === 'CLOSED' && (
                <>
                  <Badge status="error" /> {'Closed'}
                </>
              )}
            </Space>
          </Space>
        </Space>

        <Row justify={'end'}>
          <Space>
            {data?.status === 'OPEN' && (
              <Popconfirm
                title="Close job post"
                description="Are you sure you want to close the job post?"
                open={isCloseStatusConfirmOpen}
                onConfirm={handleUpdate({ status: 'CLOSED' })}
                onCancel={handleCloseStatusCancel}
              >
                <Button
                  type="text"
                  shape="round"
                  onClick={showCloseStatusPopconfirm}
                >
                  Close
                </Button>
              </Popconfirm>
            )}
            {data?.status === 'CLOSED' && (
              <Popconfirm
                title="Open job post"
                description="Are you sure you want to open the job post?"
                open={isOpenStatusConfirmOpen}
                onConfirm={handleUpdate({ status: 'OPEN' })}
                onCancel={handleOpenStatusCancel}
              >
                <Button
                  type="text"
                  shape="round"
                  onClick={showOpenStatusPopconfirm}
                >
                  Open
                </Button>
              </Popconfirm>
            )}
          </Space>
        </Row>
      </div>
    );
  }, [data, isOpenStatusConfirmOpen, isCloseStatusConfirmOpen]);

  /*
   * What this card used to be: two logo images, one sentence, a repeat of the
   * required skills already listed further down the page, and an outline
   * button. Sixty per cent of the hero, saying almost nothing.
   *
   * What a recruiter opening a job actually wants is the other half of the
   * story -- the job describes what it asks for, and this says what it has
   * produced. Every number here already existed in the API; the page simply
   * never asked. It mentioned a candidate exactly once, as the text on a
   * button.
   *
   * The aggregation is reused from the dashboard rather than reimplemented.
   * That makes the import direction jobs -> dashboard, where dashboard already
   * imports jobs' statusPalette; the coupling between small-apps is a real
   * smell and worth a shared home eventually, but duplicating tested counting
   * logic to avoid it would be worse.
   */
  const outcomeCard = useMemo(() => {
    const pipeline = summarisePipeline(
      data ? [{ ...data, status: data.status }] : [],
      candidates.rows,
    );
    const scores = distributeScores([], candidates.rows);
    const waiting =
      pipeline.stages.find((s) => s.stage === 'HM_REVIEW')?.count ?? 0;
    const notice = matchingNotice(data);
    const paused = isMatchingPaused(data);

    return (
      <div className={styles.outcomeCard}>
        <div>
          <h2 className={styles.outcomeTitle}>Candidates</h2>

          {/* Matching limits: a paused or failed job says so, and a resumed
              one says it resumed - never a silent change. */}
          {notice && (
            <Alert
              type={notice.tone}
              showIcon
              message={notice.title}
              description={notice.detail}
              className={styles.matchingNotice}
            />
          )}

          {candidates.loading && (
            <p className={styles.outcomeEmpty}>Loading candidates…</p>
          )}

          {/* A failed request must never render as zero. Nothing about this
              job's pipeline is known, and saying "0 candidates" would be a
              claim rather than an absence. */}
          {!candidates.loading && candidates.failed && (
            <p className={styles.outcomeEmpty}>
              Could not load candidates for this job, so none of the figures
              below could be calculated.
            </p>
          )}

          {!candidates.loading && !candidates.failed && (
            <>
              <div className={styles.outcomeFigures}>
                <div>
                  <dt className={styles.outcomeLabel}>Total</dt>
                  <dd className={styles.outcomeValue}>
                    {candidates.total.toLocaleString('en-US')}
                  </dd>
                </div>
                <div>
                  <dt className={styles.outcomeLabel}>In pipeline</dt>
                  <dd className={styles.outcomeValue}>{pipeline.inPipeline}</dd>
                </div>
                <div>
                  <dt className={styles.outcomeLabel}>Awaiting you</dt>
                  <dd className={styles.outcomeValue}>{waiting}</dd>
                </div>
                <div>
                  <dt className={styles.outcomeLabel}>Best fit</dt>
                  <dd className={styles.outcomeValue}>
                    {scores.scored > 0 ? (
                      Math.max(
                        ...candidates.rows
                          .map((c) => c.fitScore)
                          .filter((s): s is number => typeof s === 'number'),
                      )
                    ) : (
                      <span className={styles.outcomeNone}>—</span>
                    )}
                  </dd>
                </div>
              </div>

              {candidates.total === 0 && (
                // No timing and no "is running" claim: nothing here can tell
                // matching in progress from nothing matched. A paused job
                // (matchingStatus PAUSED_*) gets neither the hint nor the
                // "none matched" line, because it is not being matched.
                <TrainingWheelsHint
                  storageKey="job-no-candidates"
                  message="Candidates will appear here as they're matched to this job."
                  // A paused job is not being matched, so it must not get
                  // this promise (#115's own condition).
                  enabled={data?.status === 'OPEN' && !paused}
                  anchor={
                    <p className={styles.outcomeEmpty}>
                      {paused
                        ? 'No candidates yet: matching is paused for this job.'
                        : 'No candidates matched to this job yet.'}
                    </p>
                  }
                />
              )}

              {scores.unscored > 0 && (
                <p className={styles.outcomeNote}>
                  {scores.unscored} of {candidates.rows.length} carry no fit
                  score yet — counted separately, not as zero.
                </p>
              )}

              {candidates.partial && (
                <p className={styles.outcomeNote}>
                  Figures cover the {candidates.rows.length} most recent of{' '}
                  {candidates.total}.
                </p>
              )}
            </>
          )}
        </div>

        <Button
          type="primary"
          size="large"
          className={styles.outcomeCta}
          disabled={candidates.loading}
        >
          <Link to="candidates">
            {candidates.failed || candidates.total === 0
              ? 'View candidates'
              : `View ${candidates.total.toLocaleString('en-US')} candidates`}
          </Link>
        </Button>
      </div>
    );
  }, [data, candidates]);

  return (
    <Layout user={user}>
      <Breadcrumb
        renderLeft={
          <Button type="text" icon={<ArrowLeftOutlined />}>
            <Link to="..">Back to job list</Link>
          </Button>
        }
      />
      {!data ? (
        <Flex align="center" justify="center" className={styles.loading}>
          <Spin tip="Loading..." />
        </Flex>
      ) : (
        <>
          <div className={styles.header}>
            {ProductManagerCard}
            {outcomeCard}
          </div>
          <div className={styles.hmCardWrapper}>
            <div className={styles.card}>
              {!data.hiringManagerName && !data.recruiterName ? (
                <>
                  <Typography className={styles.cardProse}>
                    Assign a Hiring Manager or Recruiter to this job listing.
                    Team members attached to the job will be visible only to you
                    and the HR Executive.
                  </Typography>
                  {canSeeJobAssignHMCard && (
                    <Button onClick={handleModalOpen}>
                      Assign team member
                    </Button>
                  )}
                </>
              ) : (
                <>
                  <Space size={4} direction="vertical">
                    {data.hiringManagerName && (
                      <Space>
                        <Typography>Hiring manager:</Typography>
                        <Typography.Text strong>
                          {data.hiringManagerName}
                        </Typography.Text>
                      </Space>
                    )}
                    {data.recruiterName && (
                      <Space>
                        <Typography>Recruiter:</Typography>
                        <Typography.Text strong>
                          {data.recruiterName}
                        </Typography.Text>
                      </Space>
                    )}
                  </Space>
                  {canSeeJobAssignHMCard && (
                    <Button onClick={handleModalOpen}>
                      Edit team assignment
                    </Button>
                  )}
                </>
              )}
            </div>
          </div>
          <JobDetailsEdit data={data} onUpdateDetails={handleJobUpdate} />
          <SkillsExperience data={data} />
        </>
      )}
      <HiringManagerModal
        isOpen={isModalOpen}
        data={hiringManagers}
        isLoading={isLoading}
        hiringManagerId={data?.hiringManagerId}
        recruiterId={data?.recruiterId}
        onClose={handleModalClose}
        onFetchHiringManagers={onFetchHiringManagers}
        onFetchRecruiters={onFetchRecruiters}
        onAssignHiringManagers={onAssignHiringManagers}
        onAssignRecruiter={onAssignRecruiter}
      />
    </Layout>
  );
};

export default JobDetails;
